import { useState } from "react";
import {
  Button,
  Flex,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Icon,
  IconType,
  ListItemButton,
  Box
} from "flux";
import Page from "@adora/shared/components/layout/page";
import { CommonRadioGroup } from "../../../web/src/components/common/common-radio-group";
import { platformDisplayNames } from "@adora/shared/features/ad-publishing/helpers";
import { Platform, PLATFORMS } from "@adora/shared/types/ad-platforms";
import FormControl from "../../../web/src/features/ad-builder/form-control";
import { useParams, useSearchParams } from "react-router-dom";
import useBrandName from "../hooks/use-brand-name";
import {
  // useMutateCreateReportingTrackedIds,
  useReportingTrackedIds,
  useMutateUpdateReportingTrackedIds,
  useMutateCreateReportingTrackedIds
} from "../hooks/use-reporting-tracked-ids";
import { ReportingIds } from "../types/reporting-ids";
import { useReportingStore } from "../store/reporting-ids-store";

const TitleBarActions = ({
  brandId,
  selectedPlatform,
  data
}: {
  brandId: string | undefined;
  selectedPlatform: Platform;
  data: ReportingIds[];
}) => {
  const { platforms } = useReportingStore();
  const currentPlatform = PLATFORMS[selectedPlatform.toUpperCase()] as Platform;
  const { trigger: createTrigger, isMutating: isCreateMutating } = useMutateCreateReportingTrackedIds();
  const { trigger: updateTrigger, isMutating: isUpdateMutating } = useMutateUpdateReportingTrackedIds(
    data[0]?.id?.toString()
  );

  const isDataEmpty = data.length === 0;
  const isCampaignIdsEmpty = !isDataEmpty && data.map((item) => item.campaignIds).length === 0;
  const isAdgroupIdsEmpty = !isDataEmpty && data.map((item) => item.adgroupIds).length === 0;
  const handleSave = async () => {
    //check if data is empty
    if (!isDataEmpty && !isCampaignIdsEmpty && !isAdgroupIdsEmpty) {
      updateTrigger({
        brandId,
        campaignIds: platforms[currentPlatform]?.campaignIds || [],
        adgroupIds: platforms[currentPlatform]?.adgroupIds || []
      });
    } else {
      createTrigger({
        brandId,
        platform: currentPlatform,
        campaignIds: platforms[currentPlatform]?.campaignIds || [],
        adgroupIds: platforms[currentPlatform]?.adgroupIds || []
      });
    }
  };

  return (
    <Button
      color="primary"
      variant="contained"
      sx={{ paddingLeft: "20px", paddingRight: "20px" }}
      onClick={handleSave}
      loading={isCreateMutating || isUpdateMutating}
      dataDogIdentifier="report settings save"
    >
      Save Changes
    </Button>
  );
};

type PlatformValue = (typeof platformDisplayNames)[keyof typeof platformDisplayNames];

const ReportSettings = () => {
  const [, setSearchParams] = useSearchParams();
  const { brandId } = useParams<{ brandId: string }>();
  const brandName = useBrandName(brandId);
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformValue>("GDN");
  const { data } = useReportingTrackedIds(brandId, PLATFORMS[selectedPlatform.toUpperCase()]);

  const currentPlatform = PLATFORMS[selectedPlatform.toUpperCase()] as Platform;

  return (
    <Page
      title="Report Settings"
      description="Configure the brands reporting settings for each ad platform"
      backLinkText={brandName}
      titleBarActions={
        <TitleBarActions brandId={brandId} selectedPlatform={selectedPlatform as Platform} data={data} />
      }
      testId="report-settings-header"
    >
      <CommonRadioGroup
        options={Object.values(platformDisplayNames)}
        selectedValue={selectedPlatform}
        setSelectedValue={(value) => {
          setSelectedPlatform(value as Platform);
          setSearchParams({ platform: value as Platform });
        }}
      />
      <Flex flexDirection="column" gap="4px" mt="84px" px="48px">
        <Typography variant="h4">What to include in {selectedPlatform} reports?</Typography>
        <Typography variant="body1">
          Add the IDs for any platform campaigns or ad groups you want to track for reporting. If you include a campaign
          ID, all its ad groups will be automatically tracked, so you won&apos;t need to add each ad group individually.
        </Typography>
      </Flex>
      <Flex flexDirection="row" gap="16px" mt="82px" alignItems="flex-start" justifyContent="center">
        <ReportingField label="Campaigns" fieldType="campaign" platform={currentPlatform} />
        <ReportingField label="Ad Groups" fieldType="adgroup" platform={currentPlatform} />
      </Flex>
    </Page>
  );
};

const ReportingField = ({
  label,
  fieldType,
  platform
}: {
  label: string;
  fieldType: "campaign" | "adgroup";
  platform: Platform;
}) => {
  const [textFieldValue, setTextFieldValue] = useState("");

  // Get store data and actions
  const { platforms, setCampaignIds, setAdgroupIds } = useReportingStore();
  // Select the right data and update function based on field type
  const ids = fieldType === "campaign" ? platforms[platform]?.campaignIds || [] : platforms[platform]?.adgroupIds || [];

  const updateIds = (newIds: string[]) => {
    if (fieldType === "campaign") {
      setCampaignIds(platform, newIds);
    } else {
      setAdgroupIds(platform, newIds);
    }
  };

  const handleAdd = () => {
    if (textFieldValue.trim()) {
      // Add the new ID to the existing IDs
      const newIds = [textFieldValue, ...ids];
      updateIds(newIds);
      setTextFieldValue("");
    }
  };

  const handleDelete = (index: number) => {
    // Remove the ID at the specified index
    const newIds = [...ids];
    newIds.splice(index, 1);
    updateIds(newIds);
  };

  return (
    <Flex flexDirection="column" gap="4px">
      <Flex flexDirection="row" alignItems="center">
        <FormControl displayName={label} errors={[]}>
          <TextField
            value={textFieldValue}
            onChange={(e) => setTextFieldValue(e.target.value)}
            placeholder={`Enter IDs...`}
            sx={{ "& .MuiInputBase-root": { height: "40px", width: "452px" } }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleAdd();
                e.preventDefault();
              }
            }}
            dataDogIdentifier={`report settings ${label.toLowerCase()}`}
          />
        </FormControl>
        <Button
          size="medium"
          variant="contained"
          color="secondary"
          sx={{ marginBottom: "4px" }}
          onClick={handleAdd}
          dataDogIdentifier="report settings add"
        >
          Add
        </Button>
      </Flex>
      <Box width="100%">
        <List
          sx={{
            width: "100%",
            padding: 0,
            ...(ids.length > 0 && {
              border: "1px solid #E0E0E0",
              borderRadius: "4px"
            }),
            ...(ids.length >= 4 && {
              height: "229px",
              overflow: "auto"
            })
          }}
        >
          {ids.length === 0 && (
            <ListItem>
              <ListItemText>No {label} IDs </ListItemText>
            </ListItem>
          )}
          {ids.length > 0 &&
            ids.map((id, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton onClick={() => handleDelete(index)} dataDogIdentifier="report settings delete">
                    <Icon name={IconType.TrashCan} />
                  </IconButton>
                }
              >
                <ListItemButton sx={{ borderRadius: "4px" }}>
                  <ListItemText>{id}</ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Box>
    </Flex>
  );
};

export default ReportSettings;
